/**
 * App Sidebar Component
 * WHY: Main navigation sidebar with sections, badges, locked items, and identity block
 *
 * @version v0.13 (2026-10-08, cc_tasks row 520, T560)
 *   v0.13: the START HERE card is hidden while the quiet first-screen line shows
 *          (OnboardingResumeBanner v1.1, src/lib/trialFacts.ts quietLineShows), and
 *          while that is still being worked out, so it never flashes in and out.
 * @version v0.12 (2026-06-19)
 *   v0.12: CRM group now uses a dedicated crmOpen useState(false) — identical to
 *          Journalism/Recruit — instead of the persisted navCollapsed map. The
 *          map could hold crm:false and pin the group OPEN every load (recurring
 *          "stuck open" regression). Now CRM defaults COLLAPSED every session.
 * @version v0.11 (2026-06-01)
 *   v0.11: Fix INVEST nav active-highlight + collapse bug. Exact (`end`)
 *          NavLink matching so each route highlights its own item; "#"/locked
 *          items (e.g. APIs & AI Agents) render as plain anchors — never
 *          route-active. Collapsible groups (Invest/Discover/Journalism) stay
 *          open when the current route is inside them; manual toggle respected.
 * @version v0.10 (2026-06-01)
 *   v0.10: Journalism group collapsed-by-default via dedicated journalismOpen
 *          state (mirrors Invest's investOpen pattern). Earned Media item gets
 *          an ENTERPRISE pill (reuses SidebarMenuBadge amber styling) + stays
 *          muted/greyed for non-superadmins. Visual only — routes/gates intact.
 * @version v0.9 (2026-05-11)
 *   v0.9: NAV Polish v2 — 22 items → ~15-17. WATCH section killed
 *         (Markets + Pipeline Watch moved to DISCOVER). Stealth Approach
 *         moved VET → ENGAGE (it's outreach, not vetting). Bulk Compare
 *         dissolved into Compare People mode tab. Saved Dossiers renamed
 *         to Saved Content (unified surface, 8 filter tabs). Sales
 *         Arsenal + Launch Pack + Apply to Programs removed from main
 *         NAV (still routable; live inside Dens). My Team + Inbox removed
 *         from main NAV (still routable; live as Dashboard sub-tabs).
 *         Five canonical sections: Discover / Vet / Engage / Setup +
 *         Extras secondary rail. (Watch killed.)
 *   v0.8: NAV Restructure — 43 items → 22, verb-grouped.
 *         DISCOVER / VET / ENGAGE / WATCH / SETUP.
 *         Vetting Suite section collapsed into single master link
 *         (/dashboard/vetting-suite, 13 cards). Setup section collapsed
 *         into single master link (/dashboard/setup, 11 items). HR section
 *         deleted entirely — items distributed to VET + ENGAGE. CRYPTO &
 *         INVESTMENTS deleted — Markets + Pipeline Watch moved to WATCH;
 *         PE/VC Firm Vetters moved to Vetting Suite master. PEOPLE &
 *         COMPANIES deleted — People Finder + Company Finder moved to
 *         DISCOVER, Saved Dossiers moved to VET. ANALYZE deleted — Reports
 *         relocated to /dashboard/reports + a Dashboard Reports section.
 *         Welcome now hides on welcome_acknowledged_at NOT NULL.
 *         CC Queue + Admin Dashboard gated on is_superadmin (NOT is_admin).
 *         Old URLs all continue to work; only sidebar links reorganized.
 *   v0.7: + Welcome Inbox entry under Dashboard (red dot when unviewed
 *         items exist, hidden once all viewed). PH Bundle 0.1.
 *   v0.6: 9-group reorg per Saul (replaced by v0.8).
 *   v0.5: + Connections entry under Setup
 *   v0.4: + Markets top-level entry
 *   v0.3: + Talent CRM under Intelligence
 *   v0.2: + Bulk Compare under Intelligence
 *   v0.1: original
 */

import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { AppLayoutContext } from "@/components/layout/AppLayoutContext"; // T560
import { FoxApisModal } from "@/components/FoxApisModal";
import { useNavigate, useLocation } from "react-router-dom";
import { useSuperadmin } from "@/hooks/useSuperadmin";
import { useAdminView } from "@/components/layout/AdminViewContext";
import {
  LayoutDashboard,
  Building2,
  Users2,
  Newspaper,
  Award,
  TrendingUp,
  FileText,
  FileCheck,
  Tag,
  Search,
  Users,
  Settings,
  ChevronDown,
  ChevronRight,
  Zap,
  Eye,
  EyeOff,
  UserSearch,
  Swords,
  HelpCircle,
  User,
  Shield,
  ShieldCheck,
  Crown,
  Bookmark,
  PenTool,
  Mail,
  Inbox,
  Binoculars,
  Building,
  DollarSign,
  BarChart3,
  Megaphone,
  Cpu,
  Plug,
  KeyRound,
  Scale,
  GitCompare,
  LineChart,
  ChartLine,
  Sparkles,
  NotebookPen,
  ClipboardCheck,
  Bot,
  Star,
  Brain,
  Target,
  MessageSquare,
  Radar,
  FolderLock,
  ListChecks,
  MessagesSquare,
  BookOpen,
  Video,
  Briefcase,
  HandHeart,
  Handshake,
  Gavel,
  Banknote,
  PiggyBank,
  Stethoscope,
  PawPrint,
  Pill,
  Rocket,
  Calendar,
  KanbanSquare,
  Quote,
  Crosshair,
  Radio,
  LogOut,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { NavLink } from "@/components/NavLink";
import { Badge } from "@/components/ui/badge";
import { useNavCounts } from "@/hooks/useNavCounts";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { safeCount } from "@/lib/safeCount";
import { useTrialFacts, quietLineShows, setupDone } from "@/lib/trialFacts"; // T560
import { SidebarIdentityBlock } from "@/components/layout/SidebarIdentityBlock";
import { shouldShowRouteForDen } from "@/config/den-route-map";
import { useRecordMode } from "@/hooks/useRecordMode";
import { setInvestMode as persistInvestMode, hasInvestorAccess, investIsOff, type InvestMode } from "@/lib/investMode";
// WhatsNewPanel relocated to TopBar — kept here as a comment so the
// import diff explains the move. See TopBar.tsx for the new mount.
import type { Tables } from "@/integrations/supabase/types";
import { FoxMark, FOX_ICON, IconGlyph } from "@/components/brand/FoxMark";

type Profile = Tables<"profiles">;

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  badge?: string;
  locked?: boolean;
  external?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  /** data-foxchat-id the rendered link receives — enables window.foxchat.highlight(id). */
  foxchatId?: string;
  /** Highlight key for the "what's new" light. Not a DB column — local only. */
  lightKey?: string;
  /**
   * public.nav_counters key. When set, this item can carry a count pill showing
   * how many ACTIONABLE things are waiting on it — never a potential, never a
   * raw row count, never anything archived. The pill sits on the suite heading
   * while the suite is collapsed and moves onto this item when it opens.
   */
  countKey?: string;
  /** Optional richer hover tooltip. Falls back to `name` when unset. */
  tooltip?: string;
  /**
   * INVEST-MODE GATE (Dispatch D, 2026-05-23). When true, this item only shows
   * for users whose invest_mode is 'investor' or 'both'. Keying the gate on a
   * per-item flag (rather than hardcoding names in the filter) makes adding
   * future investor-only items — e.g. Deal Memos (Dispatch K) — trivial: just
   * set requiresInvestMode on the item. Items WITHOUT the flag are always
   * visible (Den, Dealflow, Outreach, Scans, Vetters, GEO/SEO, Settings).
   */
  requiresInvestMode?: boolean;
  /**
   * When set, the item renders visible-but-disabled (greyed, via `locked`)
   * with this text in its tooltip + a "Soon" badge — used for features that
   * are admin-only today but advertised as coming (e.g. Earned Media for
   * non-superadmin users). Pair with `locked: true`.
   */
  comingSoonLabel?: string;
}

interface NavSection {
  section: string;
  items: NavItem[];
  /** Stable key for persisting collapse state in user_preferences.nav_collapsed. */
  key?: string;
  /** Emoji prefix rendered next to the section label when collapsible. */
  emoji?: string;
  /** Whether this group can be collapsed by the user. Top-level sections (Dashboard) skip this. */
  collapsible?: boolean;
}

interface AppSidebarProps {
  profile: Profile | null;
  newMentionsCount?: number;
  actionItemsCount?: number;
}

/**
 * Single source of truth for whether a collapsible nav group renders collapsed.
 *
 * INVARIANT (guards the recurring CRM "stuck open" regression, 2026-06-14):
 *   A group is OPEN only if its current route is inside it (routeActive) OR the
 *   user has explicitly opened it (manualOpen). Otherwise it is COLLAPSED.
 *
 * Consequences this invariant locks in:
 *   • A group whose manualOpen defaults to false (e.g. CRM, which is excluded
 *     from routeActive) renders COLLAPSED on first load and toggles freely.
 *   • The toggle can always CLOSE a group, because no route can force it open
 *     when that group is excluded from routeActive.
 *
 * Pure + dependency-free on purpose so the behavior is testable in isolation
 * and self-documenting. Do not inline this back into JSX — keeping it named is
 * the guard. (No vitest/jest in this repo, so this is the code-level invariant
 * in lieu of a unit test; if a test runner is added, assert:
 *   computeGroupCollapsed(false, false) === true    // CRM default → collapsed
 *   computeGroupCollapsed(false, true)  === false   // user opened → open
 *   computeGroupCollapsed(true,  false) === false ) // route inside → open
 */
export function computeGroupCollapsed(routeActive: boolean, manualOpen: boolean): boolean {
  return !(routeActive || manualOpen);
}

// INVEST mode-switch (2026-05-21): the INVEST section serves two audiences.
// Same routes, mode-appropriate labels. Founder "Raise Pipeline" points at
// the pipeline-crm founder view; investor "Pipeline" at the investor surface.
// INVEST-MODE GATE (Dispatch D): investor-only items carry requiresInvestMode
// so they only show when invest_mode is 'investor' or 'both'. Always-visible
// items (Pipeline, Signals, Outreach, vetting suite shortcut) omit the flag.
const INVEST_ITEMS_INVESTOR: NavItem[] = [
  { name: "Overview", href: "/dashboard/invest", icon: LayoutDashboard, foxchatId: "nav-invest-overview", requiresInvestMode: true },
  { name: "Pipeline", href: "/dashboard/pipeline", icon: KanbanSquare, foxchatId: "nav-pipeline" },
  // MARKETS-RETIRE (2026-07-24): Pipeline Watch used to live under the retired
  // /markets section with no INVEST entry at all. It surfaces here, on the
  // investor side, pointing at the watchlist tab that already mounts the same
  // component — no new page, no new top-level nav.
  { name: "Pipeline Watch", href: "/dashboard/pipeline?tab=watchlist", icon: Binoculars, foxchatId: "nav-pipeline-watch", requiresInvestMode: true, tooltip: "Pre-IPO and breakout watch lists — about to IPO, lockup expiry, Series B/C/D breakout, emerging pre-Series-A" },
  { name: "Signals", href: "/dashboard/signals", icon: Radio, foxchatId: "nav-signals" },
  { name: "Outreach", href: "/dashboard/outreach", icon: Megaphone, foxchatId: "nav-outreach" },
  { name: "Find Investors", href: "/dashboard/invest/find-investors", icon: Search, foxchatId: "nav-find-investors", requiresInvestMode: true },
  { name: "Pipeline Match", href: "/dashboard/invest/pipeline-match", icon: Target, foxchatId: "nav-pipeline-match", requiresInvestMode: true },
  { name: "Tweet Hunter", href: "/dashboard/invest/tweet-hunter", icon: Crosshair, foxchatId: "nav-tweet-hunter", requiresInvestMode: true },
  { name: "Founder Radar", href: "/dashboard/invest/founder-radar", icon: Radar, foxchatId: "nav-founder-radar", requiresInvestMode: true, badge: "New" },
  { name: "Warm Intros", href: "/dashboard/invest/warm-intros", icon: Handshake, foxchatId: "nav-warm-intros", requiresInvestMode: true },
  { name: "Sector Theses", href: "/dashboard/invest/sector-theses", icon: Crosshair, foxchatId: "nav-sector-theses", requiresInvestMode: true },
  { name: "Portfolio", href: "/dashboard/invest/portfolio", icon: PiggyBank, foxchatId: "nav-portfolio", requiresInvestMode: true },
  { name: "Fund Sim", href: "/dashboard/invest/fund-sim", icon: TrendingUp, foxchatId: "nav-fund-sim", requiresInvestMode: true, badge: "New" },
  // INVEST-IA v0.01 (2026-05-29): Portfolio Watch + Portfolio Pulse folded
  // into the Signals page as tabs (Watching, Digest). Old routes redirect.
  { name: "Anti-Portfolio", href: "/dashboard/pipeline?tab=passed", icon: GitCompare, foxchatId: "nav-anti-portfolio", requiresInvestMode: true },
  { name: "Term Sheets", href: "/dashboard/invest/term-sheets/new", icon: Scale, foxchatId: "nav-term-sheets", requiresInvestMode: true },
  { name: "Deal Memos", href: "/dashboard/invest/memos", icon: NotebookPen, foxchatId: "nav-deal-memos", requiresInvestMode: true },
  { name: "Diligence", href: "/dashboard/invest/diligence", icon: ClipboardCheck, foxchatId: "nav-diligence", requiresInvestMode: true, badge: "New" },
  { name: "Linchpins", href: "/dashboard/invest/linchpins", icon: Zap, foxchatId: "nav-linchpins", requiresInvestMode: true },
  { name: "Investor Circuit", href: "/dashboard/invest/circuit", icon: Users, foxchatId: "nav-investor-circuit", requiresInvestMode: true },
  { name: "Founder · Investor · Firm", href: "/dashboard/vetting-suite?type=founder,investor,firm", icon: Banknote, foxchatId: "nav-founder-investor-firm" },
];
const INVEST_ITEMS_FOUNDER: NavItem[] = [
  { name: "Raise Pipeline", href: "/dashboard/pipeline-crm?mode=founder", icon: KanbanSquare, foxchatId: "nav-raise-pipeline" },
  { name: "Signals", href: "/dashboard/signals", icon: Radio, foxchatId: "nav-signals" },
  { name: "Outreach", href: "/dashboard/outreach", icon: Megaphone, foxchatId: "nav-outreach" },
  { name: "Find Investors", href: "/dashboard/invest/find-investors", icon: Search, foxchatId: "nav-find-investors" },
  { name: "Raise Simulator", href: "/dashboard/invest/raising-sim", icon: TrendingUp, foxchatId: "nav-raising-sim", badge: "New" },
  { name: "Warm Intros", href: "/dashboard/invest/warm-intros", icon: Handshake, foxchatId: "nav-warm-intros" },
  { name: "My Thesis", href: "/dashboard/my-thesis?mode=founder", icon: Crosshair, foxchatId: "nav-my-thesis" },
  { name: "My Cap Table", href: "/dashboard/my-cap-table?mode=founder", icon: PiggyBank, foxchatId: "nav-my-cap-table" },
  { name: "Investor Updates", href: "/dashboard/invest/updates", icon: Mail, foxchatId: "nav-investor-updates", badge: "New" },
  { name: "Outreach Templates", href: "/dashboard/invest/outreach-templates", icon: Mail, foxchatId: "nav-outreach-templates", badge: "New" },
  { name: "Decks", href: "/dashboard/invest/decks", icon: FileText, foxchatId: "nav-investor-decks", badge: "New" },
  { name: "Data Rooms", href: "/dashboard/invest/datarooms", icon: FolderLock, foxchatId: "nav-investor-datarooms", badge: "New" },
  { name: "Founder Stack", href: "/dashboard/founder/stack", icon: LayoutDashboard, foxchatId: "nav-founder-stack" },
  { name: "Anti-Investors", href: "/dashboard/anti-investors?mode=founder", icon: GitCompare, foxchatId: "nav-anti-investors" },
  { name: "Investor · Founder · Firm", href: "/dashboard/vetting-suite?type=founder,investor,firm", icon: Banknote, foxchatId: "nav-investor-founder-firm" },
];

export function AppSidebar({ profile, newMentionsCount = 0, actionItemsCount = 0 }: AppSidebarProps) {
  // WHAT IS WAITING, AND ONLY IF YOU CAN ACT ON IT. Counts, seen-state and the
  // clear-on-leave rule all live in the hook; the sidebar only decides WHERE the
  // pill sits — heading while collapsed, item once open.
  const navCounts = useNavCounts();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const [isAdmin, setIsAdmin] = useState(false);
  const [foxApisOpen, setFoxApisOpen] = useState(false);
  const [ccPendingCount, setCcPendingCount] = useState(0);
  const [engagePendingCount, setEngagePendingCount] = useState(0);
  const [dataQualityCount, setDataQualityCount] = useState(0);
  // PH Bundle 0.1 — count of welcome_inbox_items NOT yet viewed (for red dot).
  // When zero, the Welcome nav item is hidden entirely.
  const [welcomeUnviewedCount, setWelcomeUnviewedCount] = useState(0);
  const [welcomeTotalCount, setWelcomeTotalCount] = useState(0);
  // NAV-WAVE-1 (2026-05-19): badge counter for Saved Content. Compares
  // saved_items.saved_at against localStorage.saved_content_last_visit_at
  // and shows the number of items saved since the user last visited the
  // page. Cleared on visit.
  const [savedContentBadgeCount, setSavedContentBadgeCount] = useState(0);
  // NAV Restructure v0.8 — Welcome nav item also hides once the user has
  // acknowledged Welcome (profiles.welcome_acknowledged_at IS NOT NULL).
  // This is set on first card-open per Welcome v0.4. Saul already has
  // this set to 2026-05-10 via the #170 migration backfill.
  const welcomeAcked = !!(profile as any)?.welcome_acknowledged_at;
  const [badgePulsing, setBadgePulsing] = useState(false);
  const prevEngageCount = useRef(0);
  // Current pathname held in a ref so the engagement-count effect can read it for
  // the pulse check WITHOUT taking location.pathname as a dependency (which would
  // re-run the effect — re-fetch + re-subscribe — on every route change/render).
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  // Stable primitive used as the dependency for every sidebar count-badge effect,
  // so a churning `profile` object reference can't re-run them and storm the pool.
  const profileId = profile?.id;

  // ALL-SUITES-COLLAPSED (2026-09-01): every collapsible nav group — INVEST
  // included, no exceptions — starts COLLAPSED on every load. One session-scoped
  // map is the SOLE control: an empty map means nothing is open, so there is no
  // "undefined means open" hole and no persisted map that can pin a suite open.
  // Toggles still persist to user_preferences.nav_groups_collapsed for history,
  // but the persisted value is never read back as an initial open state.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [collapseLoaded, setCollapseLoaded] = useState(false);
  // Den count drives EHQ visibility:
  //   0 Dens → EHQ standalone under Discover
  //   1 Den  → EHQ embedded inside Den; sidebar entry hidden
  //   2+ Dens → each Den manages its own; no top-level EHQ
  const [denCount, setDenCount] = useState<number | null>(null);

  // DEN-SWITCHER-PILL (2026-05-17) — active Den + "show all surfaces" flag
  // drive sidebar filtering via DEN_ROUTE_AFFINITIES. The pill in the top
  // bar dispatches a 'den-state-changed' CustomEvent on switch / toggle;
  // we re-query and re-render. Until the first load resolves, every item
  // is visible (over-show > under-show during first paint).
  const [activeDenKey, setActiveDenKey] = useState<string | null>(null);
  const [showAllSurfaces, setShowAllSurfaces] = useState(false);
  const [denFilterLoaded, setDenFilterLoaded] = useState(false);

  // INVEST mode-switch (2026-05-21): collapsed by default; first click shows
  // a Founder/Investor selector; the pick persists to user_preferences and
  // tailors the sub-nav. Re-clicking the header never collapses — a "Switch
  // mode" link at the bottom reopens the selector.
  const [investMode, setInvestModeState] = useState<InvestMode | null>(null);
  const [investOpen, setInvestOpen] = useState(false);
  const [investSelectorOpen, setInvestSelectorOpen] = useState(false);
  // JOURNALISM / RECRUIT / CRM / SIGNALS: no longer carry their own open-state.
  // They ride the single openGroups map (ALL-SUITES-COLLAPSED, 2026-09-01),
  // which gives every suite the same collapsed-on-load behavior they each had
  // to hand-roll before.
  // RAISING-MODE v0.01 (2026-05-29) — when fundraising_mode is true, the
  // founder INVEST sidebar reorders to put fundraise-facing pages on top.
  // Read on mount from profiles.fundraising_mode. The toggle banner on
  // /dashboard/den and /dashboard/invest flips this; sidebar refreshes
  // on next navigation. (No realtime subscription needed for a setting.)
  const [fundraisingMode, setFundraisingMode] = useState<boolean>(false);

  const accountAgeDays = profile?.created_at
    ? Math.floor((Date.now() - new Date(profile.created_at).getTime()) / 86400000)
    : 999;
  const isBrandNew = accountAgeDays < 7;
  // T560: the quiet first-screen line replaces this card while it shows (undefined = still loading: hide).
  // Only AppLayout mounts the line, so only there does the card give way to it.
  const inAppLayout = useContext(AppLayoutContext);
  const trialFacts = useTrialFacts(inAppLayout && !!profile && !setupDone(profile));
  const quietLineOn = inAppLayout && quietLineShows(profile, trialFacts) !== false;

  const tier = (profile as any)?.tier || "free";
  const { isSuperadmin } = useSuperadmin();
  // RECORDING-MODE (2026-05-17): hide all admin/tier/version chrome when
  // the URL carries ?rec=1 — for public-facing walkthrough recordings.
  const recordMode = useRecordMode();
  const role = (profile as any)?.role_in_company;
  const isExecutive = role === "executive";

  // Den count for EHQ conditional rendering.
  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    (async () => {
      const count = await safeCount(
        supabase.from("user_active_dens" as any).select("id", { count: "exact", head: true }).eq("user_id", profileId),
      );
      if (alive && count != null) setDenCount(count);
    })();
    return () => { alive = false; };
  }, [profileId]);

  // DEN-SWITCHER-PILL (2026-05-17) — load active Den + show-all-surfaces
  // flag and re-load whenever the pill fires 'den-state-changed'.
  useEffect(() => {
    if (!profile) return;
    let alive = true;
    const loadDenState = async () => {
      const [denRows, prefs, profRow] = await Promise.all([
        supabase
          .from("user_active_dens" as any)
          .select("persona_key, is_primary")
          .eq("user_id", profile.id),
        supabase
          .from("user_preferences" as any)
          .select("show_all_surfaces, invest_mode")
          .eq("user_id", profile.id)
          .maybeSingle(),
        supabase
          .from("profiles")
          .select("fundraising_mode")
          .eq("id", profile.id)
          .maybeSingle(),
      ]);
      if (!alive) return;
      const rows = ((denRows.data as any) || []) as Array<{ persona_key: string; is_primary: boolean }>;
      const primary = rows.find((r) => r.is_primary) || rows[0] || null;
      setActiveDenKey(primary?.persona_key || null);
      setShowAllSurfaces(!!(prefs.data as any)?.show_all_surfaces);
      const im = (prefs.data as any)?.invest_mode;
      setInvestModeState(im === "founder" || im === "investor" ? im : null);
      setFundraisingMode(!!(profRow.data as any)?.fundraising_mode);
      setDenFilterLoaded(true);
    };
    loadDenState();
    const onChange = () => loadDenState();
    window.addEventListener("den-state-changed", onChange);
    return () => {
      alive = false;
      window.removeEventListener("den-state-changed", onChange);
    };
  }, [profile]);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!profile) return;
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", profile.id)
        .eq("role", "admin");
      setIsAdmin(roles && roles.length > 0);
    };
    checkAdminStatus();
  }, [profile]);

  // Nav-collapse load. The persisted map is NOT applied to the open state any
  // more (see ALL-SUITES-COLLAPSED above) — this only flips the flag that gates
  // the brand-new-user CTA so it does not flicker in on established accounts.
  useEffect(() => {
    if (!profile) return;
    setCollapseLoaded(true);
  }, [profile]);

  // toggleGroup opens/closes a group for this session and records the resulting
  // map. The record is write-only: on the next load every group is collapsed
  // again, so no stored value can resurrect an open suite.
  const toggleGroup = async (key: string) => {
    const next = { ...openGroups, [key]: !openGroups[key] };
    setOpenGroups(next);
    if (!profile) return;
    // Fire-and-forget — toggle stays snappy if the upsert lags. Stored inverted
    // (collapsed = !open) to match the column's existing meaning.
    const collapsedMap = Object.fromEntries(Object.entries(next).map(([k, v]) => [k, !v]));
    supabase.from("user_preferences" as any).upsert({
      user_id: profile.id,
      nav_groups_collapsed: collapsedMap,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" }).then(() => {}, () => {});
  };

  // LOG-OUT (2026-06-14): mirrors the existing TopBar.handleSignOut — clears the
  // Supabase session, then routes to /auth. Same supabase client import the rest
  // of the app uses, so the session is genuinely cleared (not a client-only nav).
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      navigate("/auth");
    }
  };

  // Unread changelog count moved to TopBar 2026-05-13 (along with the
  // What's New button + panel). See TopBar.tsx for the new owner.

  // Saul's UUID as a load-race fallback — useSuperadmin (declared near
  // top of the component) is the authoritative check; OR with isSaul so
  // the link shows on first paint even before the hook's async fetch
  // resolves.
  const { isSuperadmin: isSaul } = useSuperadmin();
  const isSuperadminCombined = isSuperadmin || isSaul;
  // T779: admin-only nav shows only while the saved "Admin view" switch is on.
  const { showAdmin } = useAdminView();
  useEffect(() => {
    if (!isSaul) return;
    const fetchCount = async () => {
      const { count } = await supabase.from("cc_tasks" as any).select("id", { count: "exact", head: true }).eq("status", "pending_approval");
      setCcPendingCount(count || 0);
    };
    fetchCount();
    const ch = supabase.channel("cc_tasks_realtime").on("postgres_changes", { event: "*", schema: "public", table: "cc_tasks" }, () => fetchCount()).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [isSaul]);

  // Engagement queue pending count + pulse on new items.
  // Depends ONLY on the stable profile id (a primitive), so it fires ONCE per
  // mount/user instead of re-running on every render where the profile object
  // identity churns or the route changes — that re-run was firing dozens of
  // identical count HEADs in the same tick and storming the connection pool.
  // The count itself is wrapped in safeCount (timeout + swallow) so a slow/503
  // count never throws or hangs — it just leaves the badge unchanged.
  useEffect(() => {
    if (!profileId) return;
    const fetchEngageCount = async () => {
      const count = await safeCount(
        supabase.from("engagement_queue" as any).select("id", { count: "exact", head: true }).eq("user_id", profileId).eq("status", "pending"),
      );
      if (count == null) return; // slow/failed count — keep the prior badge, never hang
      // Pulse if count increased and user is NOT on the engage page
      if (count > prevEngageCount.current && !pathnameRef.current.startsWith("/dashboard/engage")) {
        setBadgePulsing(true);
        setTimeout(() => setBadgePulsing(false), 5000);
      }
      prevEngageCount.current = count;
      setEngagePendingCount(count);
    };
    fetchEngageCount();
    const ch = supabase.channel("engage_queue_sidebar")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "engagement_queue", filter: `user_id=eq.${profileId}` }, () => fetchEngageCount())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "engagement_queue", filter: `user_id=eq.${profileId}` }, () => fetchEngageCount())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [profileId]);

  // RULE 12 v0.3 — pending data_quality_alerts count for sidebar badge.
  // Polls every 60s (less chatty than the engagement_queue realtime
  // subscription since the monitor cron only fires once a day).
  useEffect(() => {
    if (!profileId) return;
    const fetchDqCount = async () => {
      const count = await safeCount(
        supabase
          .from("data_quality_alerts" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", profileId)
          .eq("status", "pending"),
      );
      if (count != null) setDataQualityCount(count);
    };
    fetchDqCount();
    const interval = setInterval(fetchDqCount, 60_000);
    return () => clearInterval(interval);
  }, [profileId]);

  // Stop pulsing when navigating to engage page
  useEffect(() => {
    if (location.pathname.startsWith("/dashboard/engage")) {
      setBadgePulsing(false);
    }
  }, [location.pathname]);

  // PH Bundle 0.1 — Welcome Inbox unviewed counter.
  // Hides nav entry when total count is zero (user never had a bundle)
  // OR when all items have been viewed/actioned.
  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    const fetchWelcomeCount = async () => {
      const total = await safeCount(
        supabase.from("welcome_inbox_items" as any).select("id", { count: "exact", head: true }).eq("user_id", profileId),
      );
      const unviewed = await safeCount(
        supabase.from("welcome_inbox_items" as any).select("id", { count: "exact", head: true }).eq("user_id", profileId).is("viewed_at", null),
      );
      if (!alive) return;
      if (total != null) setWelcomeTotalCount(total);
      if (unviewed != null) setWelcomeUnviewedCount(unviewed);
    };
    fetchWelcomeCount();
    const ch = supabase.channel(`welcome_inbox_sidebar_${profileId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "welcome_inbox_items", filter: `user_id=eq.${profileId}` }, () => fetchWelcomeCount())
      .subscribe();
    return () => { alive = false; supabase.removeChannel(ch); };
  }, [profileId]);

  // ── THE LIGHT-UP ────────────────────────────────────────────────────────────
  //
  // A light means: something new and unopened that needs YOU. The counts come
  // from get_nav_lights, which derives them from the canonical message view and
  // the request pool at read time — there is no counter column here that could
  // drift away from what is actually sitting there.
  //
  // What lights is deliberately narrow: a human reply, a press request still
  // inside its deadline, an address a bounce has just retired. Digests,
  // delivery receipts and DMARC never do. The Den is not in the map at all,
  // because it always has items and a light that is always on teaches you to
  // ignore every other light in the sidebar.
  const [lights, setLights] = useState<Record<string, { count: number; why: string }>>({});
  const refreshLights = useCallback(async () => {
    if (!profileId) return;
    const { data, error } = await supabase.rpc("get_nav_lights" as any);
    if (error) { console.warn(`[AppSidebar] lights unavailable: ${error.message}`); return; }
    const next: Record<string, { count: number; why: string }> = {};
    for (const r of ((data as any[]) || [])) next[r.item_key] = { count: r.count, why: r.why };
    setLights(next);
  }, [profileId]);
  useEffect(() => { void refreshLights(); }, [refreshLights, location.pathname]);

  // Section lights are the sum of the items inside them, so a collapsed group
  // says "there is something in here" without claiming to know what.
  const sectionLight = (key: string) =>
    Object.entries(lights).filter(([k]) => k.startsWith(`${key}:`)).reduce((n, [, v]) => n + v.count, 0);

  // Opening the item is what clears it. Recorded before navigation so the light
  // is gone by the time the page renders, and a message arriving afterwards
  // lights it again.
  const clearLight = useCallback(async (itemKey: string) => {
    if (!lights[itemKey]) return;
    setLights((prev) => { const n = { ...prev }; delete n[itemKey]; return n; });
    const { error } = await supabase.rpc("mark_nav_light_seen" as any, { p_item_key: itemKey });
    if (error) { console.warn(`[AppSidebar] could not clear ${itemKey}: ${error.message}`); void refreshLights(); }
  }, [lights, refreshLights]);

  const handleFoxApisClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setFoxApisOpen(true);
  };

  // NAV-WAVE-1.5-LOOSE-ENDS (2026-05-19): Saved Content badge now
  // reads user_preferences.last_saved_content_visit_at instead of
  // localStorage so the count tracks across devices. The SavedContent
  // page stamps the column on every load.
  // Saved-content badge count. Depends only on the stable profile id (same storm
  // fix as the engagement count above) and the count HEAD is safeCount-wrapped so
  // it can't hang or throw. The route-dependent optimistic-zero is split into its
  // own cheap (no-network) effect below so navigation no longer re-fires the count.
  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    const fetchSavedCount = async () => {
      const { data: prefs } = await (supabase
        .from("user_preferences") as any)
        .select("last_saved_content_visit_at")
        .eq("user_id", profileId)
        .maybeSingle();
      const since = (prefs as any)?.last_saved_content_visit_at || "1970-01-01T00:00:00Z";
      const count = await safeCount(
        supabase
          .from("saved_items" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", profileId)
          .gt("saved_at", since),
      );
      if (!alive || count == null) return;
      setSavedContentBadgeCount(count);
    };
    fetchSavedCount();
    return () => { alive = false; };
  }, [profileId]);
  // Optimistic zero on visiting the saved-content page — cheap, no network call.
  useEffect(() => {
    if (location.pathname === "/dashboard/saved-content" || location.pathname === "/dashboard/saved") {
      setSavedContentBadgeCount(0);
    }
  }, [location.pathname]);

  // Executive gets restricted nav
  const executiveNav: NavSection[] = [
    {
      section: "",
      items: [
        { name: "Executive View", href: "/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      section: "Analyze",
      items: [
        { name: "Reports", href: "/dashboard/reports", icon: FileText },
      ],
    },
    {
      section: "",
      items: [
        { name: "Settings", href: "/dashboard/settings", icon: Settings },
      ],
    },
  ];

  // ═══════════════════════════════════════════════════════════════
  // Nav structure v3 (2026-05-04) — 8 collapsible groups + Dashboard pinned above.
  //   🎯 Discover            — find signal  (Scans, GEOFixer, SEOFixer, Engagement HQ)
  //   📥 Engage              — act on it    (Outreach, Dealflow, Inbox, Live Demo, Saved, Sales Arsenal)
  //   🛡️ Vetting Suite       — alphabetized (14 Vetters, person + entity subjects)
  //   📈 Crypto & Investments — Markets verticals (8 sub-products + Markets landing)
  //   👥 People & Companies   — discovery + comparison (People Finder, Company Finder NEW,
  //                              Compare People, Compare Companies NEW, Saved Dossiers)
  //   🧑‍💼 HR                 — Talent Finder, Talent CRM, Candidate Eval, Bulk Compare,
  //                              Stealth Approach (cross-listings: Influencer Vetter + Compare People)
  //   📊 Analyze             — understand   (Reports)
  //   ⚙️ Setup               — config       (Clients, Contacts, Connections, MCP, Settings,
  //                              Data Quality, Company Profile + Brand Kit)
  //
  // Dashboard + My Den live OUTSIDE the 8 groups (always-visible anchor).
  //
  // Cross-listings (intentional duplication for workflow context):
  //   - Specific Fund Vetter: Vetting Suite + Crypto & Investments
  //   - Influencer Vetter:    Vetting Suite + HR
  //   - Compare People:       People & Companies + HR
  //
  // Renames in v3 (route paths preserved for backward compat):
  //   Influencer Evaluator → Influencer Vetter
  //   Fox Compare         → Compare People
  //   Bulk Compare        → Bulk Compare Candidates
  // ═══════════════════════════════════════════════════════════════
  // NAV-WAVE-1 (2026-05-19): label unified to "The Den" across all
  // self-identities that map to one. Variants (founder/agency_owner/etc)
  // still drive what the Den page renders internally; only the sidebar
  // label is consistent. Users with no matching identity get no Den slot
  // AND see Outreach Review as a standalone nav item (folded into the
  // Den as a card otherwise).
  const selfIdentity = (profile as any)?.self_identity || null;
  const hasDen =
    selfIdentity === "founder" ||
    selfIdentity === "ceo_small" ||
    selfIdentity === "solopreneur" ||
    selfIdentity === "agency_owner" ||
    selfIdentity === "investor";
  const denNavItem = hasDen
    ? { name: "The Den", href: "/dashboard/my-den", icon: Target, foxchatId: "nav-the-den" }
    : null;

  // ═══════════════════════════════════════════════════════════════
  // Nav v2.0 — NAV-WAVE-1-CONSOLIDATION (2026-05-19).
  // 17 items total: 3 standalone + 3 sections × ~14 sub-items.
  //
  //   Standalone           — Dashboard, The Den, Dealflow
  //   DISCOVER             — GEO & SEO, Vetting Suite, Find People &
  //                          Companies, Person Research, Scans
  //   INVEST               — Pipeline (tabs: Active/Watchlist/Alumni/
  //                          Passed), Signals (tabs: Live Stream/
  //                          Markets), Outreach (tabs: Outbound/
  //                          Stealth/Citation/Sender Settings),
  //                          Founder · Investor · Firm (Vetting Suite
  //                          shortcut)
  //   ACCOUNT              — Setup, My Profile, Company Profile,
  //                          Saved Content (new slot + badge),
  //                          Content Creator, Integrations,
  //                          APIs & AI Agents, Help Center
  //
  // Folded under tabs (no longer standalone nav items):
  //   Pipeline Watch → Pipeline ?tab=watchlist
  //   Anti-Portfolio → Pipeline ?tab=passed
  //   Alumni Spinout Tracker → Pipeline ?tab=alumni
  //   Signal Stream → Signals ?tab=live
  //   Markets → Signals ?tab=markets
  //   Stealth Approach → Outreach ?tab=stealth
  //   Citation Outreach → Outreach ?tab=citation
  //   Outbound → Outreach ?tab=outbound
  //   Sender Settings → Outreach ?tab=sender
  //   Compare People & Companies → Find ?tab=compare
  //
  // Folded inside The Den (rendered as cards):
  //   Henhouse — internal Den card on every variant
  //   Outreach Review — internal Den card on every variant
  //   (For users with NO Den, Outreach Review appears as standalone
  //   nav item instead.)
  //
  // Hidden until built (intentionally absent from nav, routes intact):
  //   Meetings
  // ═══════════════════════════════════════════════════════════════
  const foxholeTierUnlocked = tier === "agency" || tier === "enterprise" || isSuperadminCombined;

  const fullNav: NavSection[] = [
    // ═══════════════════════════════════════════════════════════════
    // NAV v3.0 — SIDEBAR-REGROUP (2026-07): 9 groups in this order:
    //   Dashboard · Intelligence · The Den · Signals · CRM · Suites
    //   (Invest/Recruit/Journalism) · Growth · Account · Admin.
    // Pure regroup + rename of existing items — routes preserved except
    // the Dealflow split: the standalone scan-fed board becomes
    // "Prospects" (Signals, harvest lane) while the CRM "Dealflow" tab
    // keeps the worked pipeline. There is no section-nesting primitive,
    // so the three Suites sit as consecutive collapsible groups.
    // ═══════════════════════════════════════════════════════════════
    // ── DASHBOARD (lead anchor) ──────────────────────────────────────
    {
      section: "",
      items: [
        { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, foxchatId: "nav-dashboard" },
      ],
    },
    // ── INTELLIGENCE (find + vet + compare + saved) ──────────────────
    {
      section: "Intelligence",
      key: "intelligence",
      emoji: "🧠",
      collapsible: true,
      items: [
        { name: "Find People & Companies", href: "/dashboard/find", icon: UserSearch, foxchatId: "nav-find", tooltip: "Find and enrich people and companies" },
        { name: "Vetting Suite", href: "/dashboard/vetting-suite", icon: Shield, foxchatId: "nav-vetting-suite", tooltip: "Deep-dive full reports across every angle" },
        // COMPARE-MERGE (2026-07-24): the standalone Compare entry is gone —
        // Compare is now a tab inside Find People & Companies
        // (/dashboard/find?tab=compare). /dashboard/compare still redirects
        // there, so old links and bookmarks keep working.
        {
          name: "Saved Content",
          href: "/dashboard/saved-content",
          icon: Bookmark,
          foxchatId: "nav-saved-content",
          tooltip: "Everything you've saved for later",
          badge: savedContentBadgeCount > 0 ? String(savedContentBadgeCount) : undefined,
        },
      ],
    },
    // ── THE DEN (standalone anchor — prominent) ──────────────────────
    {
      section: "",
      items: [
        // NAV-RESTORE-DEN V03 (2026-06-09): Den points at /dashboard/my-den —
        // the multi-widget Founder Den (Engagement Trail as ONE bounded card,
        // RecentArtifactsWidget). NOT /dashboard/artifacts (regression 98615d7d).
        // DEN-SWITCHER-MERGE (2026-07): the header den-name chip is the switcher.
        // Here in the nav, a 0-Den user sees Engagement HQ in place of "The Den";
        // everyone else keeps "The Den" → their active Den. While denCount is
        // still loading (null) we show "The Den" to avoid a flash.
        denCount === 0
          ? { name: "Engagement HQ", href: "/dashboard/engage", icon: MessageSquare, foxchatId: "nav-engagement-hq", countKey: "engage:hq" }
          : { name: "The Den", href: "/dashboard/my-den", icon: Target, foxchatId: "nav-den", tooltip: "Your Den — the daily command surface for your work" },
        // No-Den users see Outreach Review as a standalone item; users
        // with a Den see it surfaced inside The Den page instead.
        ...(!hasDen
          ? [{ name: "Outreach Review", href: "/dashboard/outreach-review", icon: NotebookPen, foxchatId: "nav-outreach-review" }]
          : []),
        ...(!welcomeAcked && welcomeTotalCount > 0 && welcomeUnviewedCount > 0
          ? [{ name: "Welcome", href: "/welcome", icon: Sparkles, foxchatId: "nav-welcome" }]
          : []),
      ],
    },
    // ── SIGNALS (incoming lead flow) ─────────────────────────────────
    // New Signal opens the existing scan-create flow (Scans page reads
    // ?new=true to auto-open the modal). Prospects is the renamed
    // standalone scan-fed board, scoped to the harvest lane
    // (dealflow_kind='harvest_candidate') via ?kind=; the worked-lead
    // lane lives under CRM ▸ Dealflow.
    {
      section: "Signals",
      key: "signals",
      emoji: "📡",
      collapsible: true,
      items: [
        { name: "New Signal", href: "/dashboard/scans?new=true", icon: Radio, foxchatId: "nav-new-signal", tooltip: "Start a new scan — opens the scan builder in place" },
        // Foxtrails moved onto the Scans surface as a tab (2026-07) — they're
        // scan-derived, so Scans | Foxtrails live on one page. No standalone item.
        { name: "Scans", href: "/dashboard/scans", icon: Search, foxchatId: "nav-scans", tooltip: "Scan reports and Foxtrails — leads mined from scan threads" },
        { name: "Prospector", href: "/dashboard/dealflow?kind=harvest_candidate", icon: DollarSign, foxchatId: "nav-prospects", countKey: "signals:prospector", tooltip: "Freshly harvested candidates from scans and the extension" },
      ],
    },
    // ── CRM (worked pipeline + contacts/companies/sequences) ─────────
    // CRM-NAV-RESHAPE v1 (2026-06-14) + SIDEBAR-REGROUP (2026-07): the CRM
    // items point at the live Pipeline CRM page (/dashboard/pipeline-crm)
    // and its real ?tab= values. Dealflow here is the WORKED lane
    // (crm_pipeline_items surface); Clients joined this group. Collapsed
    // by default (crmOpen, default false).
    {
      section: "CRM",
      key: "crm",
      emoji: "📇",
      collapsible: true,
      items: [
        { name: "Command Center", href: "/dashboard/pipeline-crm", icon: LayoutDashboard, foxchatId: "nav-command-center", countKey: "crm:command-center", tooltip: "Opens your CRM on the Inbox. The number is how many things are on your To do list." },
        // The Inbox is what lights when someone writes, so it needs to be a
        // thing you can see lit and click. It was only reachable as a tab.
        { name: "Inbox", href: "/dashboard/pipeline-crm?tab=inbox", icon: Inbox, foxchatId: "nav-crm-inbox", lightKey: "crm:inbox", countKey: "crm:inbox", tooltip: "Mail you received. The number is conversations you have not opened yet." },
        { name: "Contacts", href: "/dashboard/pipeline-crm?tab=contacts", icon: Users2, foxchatId: "nav-crm-contacts", lightKey: "crm:contacts" },
        { name: "Companies", href: "/dashboard/pipeline-crm?tab=companies", icon: Building2, foxchatId: "nav-crm-companies" },
        { name: "Sequences", href: "/dashboard/pipeline-crm?tab=sequences", icon: ListChecks, foxchatId: "nav-crm-sequences" },
        // TWO DIFFERENT THINGS HAD ONE NAME (2026-08-13). This item said
        // "Dealflow" and its pill counted the `dealflow` table — sales leads,
        // people, the board at /dashboard/dealflow. The tab it opens renders
        // `foxhole_pipeline_items`: COMPANIES you are working as deals, a
        // different table with a different meaning. The number and the page
        // were never the same set and never could be.
        //
        // The page keeps what it renders and gets the name for it; the pill now
        // counts that same table. "Dealflow" continues to mean the lead board
        // in Engage, which is where Saul's leads actually live.
        { name: "Deal Companies", href: "/dashboard/pipeline-crm?tab=pipeline", icon: Handshake, foxchatId: "nav-crm-dealflow", countKey: "crm:deal-companies", tooltip: "Companies you're working as deals, stage by stage. Sales leads live in Dealflow under Engage — a different board." },
        { name: "Clients", href: "/clients", icon: Building2, foxchatId: "nav-clients", tooltip: "Brands you manage" },
      ],
    },
    // ── SUITES ZONE: Invest · Recruit · Journalism (consecutive) ─────
    // ── INVEST (was FOXHOLE INVESTING) ────────────────────────────────
    {
      section: "Invest",
      key: "invest",
      emoji: FOX_ICON,
      collapsible: true,
      items: [
        { name: "Pipeline", href: "/dashboard/pipeline", icon: KanbanSquare, foxchatId: "nav-pipeline", badge: foxholeTierUnlocked ? undefined : "Agency+" },
        { name: "Signals", href: "/dashboard/signals", icon: Radio, foxchatId: "nav-signals" },
        { name: "Outreach", href: "/dashboard/outreach", icon: Megaphone, foxchatId: "nav-outreach", badge: foxholeTierUnlocked ? undefined : "Agency+" },
        // INVESTOR-FEATURES-NAV-VISIBILITY (2026-05-19): surface the
        // investor-quickfire pages under Invest so they're reachable
        // in <=2 clicks. Anti-Portfolio re-uses the Pipeline ?tab=passed
        // surface — same data, no separate page.
        { name: "Find Investors", href: "/dashboard/invest/find-investors", icon: Search, foxchatId: "nav-find-investors", badge: "New" },
        { name: "Sector Theses", href: "/dashboard/sector-theses", icon: Crosshair, foxchatId: "nav-sector-theses" },
        { name: "Portfolio", href: "/dashboard/portfolio", icon: PiggyBank, foxchatId: "nav-portfolio" },
        { name: "Anti-Portfolio", href: "/dashboard/pipeline?tab=passed", icon: GitCompare, foxchatId: "nav-anti-portfolio" },
        { name: "Founder · Investor · Firm", href: "/dashboard/vetting-suite?type=founder,investor,firm", icon: Banknote, foxchatId: "nav-founder-investor-firm" },
      ],
    },
    // ── RECRUIT (Suites zone, 2nd) ───────────────────────────────────
    // Surfaces the existing (previously orphaned) recruiting pages as one
    // collapsed suite, mirroring INVEST/JOURNALISM. The Recruiter Den is the
    // suite "today" view. Per-item visibility follows the same Den filter the
    // rest of the nav uses (shouldShowRouteForDen): hr-tools / applicants rows
    // show for recruiter/founder/executive/fractional_executive (or no Den, or
    // "show all surfaces"); evaluate / compare / vetting are universal. No new
    // routes or backends — assembly only.
    {
      section: "Recruit",
      key: "recruit",
      emoji: "🧑‍💼",
      collapsible: true,
      items: [
        { name: "Recruiter Den", href: "/dashboard/dens/recruiter", icon: Target, foxchatId: "nav-recruiter-den" },
        { name: "Talent Finder", href: "/dashboard/hr-tools/talent-finder", icon: UserSearch, foxchatId: "nav-talent-finder" },
        { name: "Bulk Compare", href: "/dashboard/hr-tools/bulk-compare", icon: GitCompare, foxchatId: "nav-bulk-compare" },
        { name: "Person Research", href: "/dashboard/evaluate", icon: ClipboardCheck, foxchatId: "nav-evaluate-candidates", countKey: "recruit:evaluate" },
        { name: "Compare 1-on-1", href: "/dashboard/compare-people", icon: Users, foxchatId: "nav-compare-people" },
        { name: "Vetting Suite", href: "/dashboard/vetting-suite", icon: Shield, foxchatId: "nav-recruit-vetting-suite" },
        { name: "Talent CRM", href: "/dashboard/hr-tools/talent-crm", icon: KanbanSquare, foxchatId: "nav-talent-crm", countKey: "recruit:talent-crm" },
        // "Applicant Screen" (/dashboard/applicants/screen) removed from the menu 2026-11-02, T1304: scoring inbound
        // applicants is screening for a hiring decision, which MentionFox does not provide. Route and code kept.
      ],
    },
    // ── JOURNALISM (Suites zone, 3rd) ────────────────────────────────
    // Journalism tools, surfaced directly in the sidebar. The 3 vetters also
    // appear in Vetting Suite master (intentional dual-listing — this is the
    // discoverable home for journalism work). Earned Media is admin-only.
    {
      section: "Journalism",
      key: "journalism",
      emoji: "📰",
      collapsible: true,
      items: [
        { name: "Get Press", href: "/dashboard/press", icon: Radio, foxchatId: "nav-press", tooltip: "Find journalists and pitch them (outbound)" },
        { name: "Press Requests", href: "/dashboard/press-requests", icon: Inbox, foxchatId: "nav-press-requests", lightKey: "journalism:press-requests", countKey: "journalism:press-requests", tooltip: "Journalists seeking sources — respond with cited claims (inbound)" },
        { name: "Newsjack", href: "/dashboard/newsjack", icon: Newspaper, foxchatId: "nav-newsjack", tooltip: "Breaking stories you can tie your expertise to — pitch or post" },
        // SOURCE-CHECK (2026-07-27): sits inside the existing Journalism group,
        // not as a new top-level item. Deep-links to the tool itself.
        { name: "Source Check", href: "/dashboard/source-check#source-check-tool", icon: ShieldCheck, foxchatId: "nav-source-check", tooltip: "Before you quote someone, see what the public record says — with the exact search behind every finding" },
        { name: "Journalist Vetter", href: "/journalist-vetter", icon: Newspaper, foxchatId: "nav-journalist-vetter" },
        { name: "Publication Vetter", href: "/publication-vetter", icon: Building2, foxchatId: "nav-publication-vetter" },
        { name: "Claims Check", href: "/claims-check", icon: ShieldCheck, foxchatId: "nav-claims-check", tooltip: "Fact-check any document claim by claim: press releases, funding news, decks, articles" },
      ],
    },
    // ── GROWTH (AI visibility + search) ──────────────────────────────
    {
      section: "Growth",
      key: "growth",
      emoji: "📈",
      collapsible: true,
      items: [
        { name: "GEO & SEO", href: "/dashboard/geo-seo", icon: Brain, foxchatId: "nav-geo-seo", badge: "New", tooltip: "Grow your visibility in AI answers and search" },
      ],
    },
    // ── ACCOUNT (was CONTROL) ────────────────────────────────────────
    // Saved Content moved to Intelligence (2026-07). APIs & Integrations
    // stay as three distinct items — merging them is a separate follow-up.
    {
      section: "Account",
      key: "account",
      emoji: "⚙️",
      collapsible: true,
      items: [
        { name: "Setup", href: "/dashboard/setup", icon: Settings, foxchatId: "nav-setup" },
        { name: "My Profile", href: "/settings/profile", icon: User, foxchatId: "nav-my-profile" },
        { name: "Company Profile", href: "/dashboard/company-profile", icon: Building, foxchatId: "nav-company-profile" },
        { name: "Content Creator", href: "/dashboard/content", icon: FileText, foxchatId: "nav-content-creator" },
        // APIS-INTEGRATIONS-MERGE (2026-07): the three former items (Integrations
        // & Webhooks, API & Webhooks, APIs & AI Agents) now live as tabs on one
        // hub page. Each old route still resolves for deep links.
        { name: "APIs & Integrations", href: "/dashboard/apis-integrations", icon: Plug, foxchatId: "nav-apis-integrations", tooltip: "Connectors, your API keys & webhooks, and AI-agent APIs — all in one place" },
        { name: "Help Center", href: "/help", icon: HelpCircle, external: true, foxchatId: "nav-help" },
      ],
    },
    // ── SUPERADMIN — staff-only operations group ─────────────────────
    // Only renders when isSuperadminCombined AND not in record mode.
    // Foxhole Outbound is NOT here — it's a real user feature under
    // Foxhole Investing now.
    ...(showAdmin && !recordMode
      ? [{
          section: "Superadmin",
          key: "superadmin",
          emoji: "🛡️",
          collapsible: true,
          // ADMIN-HUB-FOLD (2026-07): CC Queue, Page Review, and Earned Media are
          // now tabs INSIDE the Admin Dashboard (/dashboard/admin), so the nav
          // group shows only one entry — keeps admin out of walkthrough videos.
          // Each old route still resolves; the hub deep-links via ?tab=.
          items: [
            { name: "Admin Dashboard", href: "/dashboard/admin", icon: Shield, foxchatId: "nav-admin-dashboard", tooltip: "All admin tools — CC Queue, Page Review, Earned Media inside" },
          ],
        } satisfies NavSection]
      : []),
  ];

  const navigation = isExecutive ? executiveNav : fullNav;

  const handleNavClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.onClick) {
      item.onClick(e);
      return;
    }
    if (item.locked) {
      e.preventDefault();
    }
  };

  const pickInvestMode = (mode: InvestMode) => {
    setInvestModeState(mode);
    setInvestSelectorOpen(false);
    setInvestOpen(true);
    if (profile?.id) persistInvestMode(profile.id, mode).catch(() => {});
  };

  // Custom render for the INVEST section: collapsed by default, mode selector
  // on first open, mode-appropriate items + a "Switch mode" link once chosen.
  const renderInvestGroup = () => {
    let baseItems = investMode === "founder" ? INVEST_ITEMS_FOUNDER : INVEST_ITEMS_INVESTOR;
    // RAISING-MODE v0.01 (2026-05-29) — when fundraising_mode is true AND
    // the user is in founder mode, reorder the founder INVEST list so the
    // fundraise-facing pages float to the TOP. Anti-Investors / Founder
    // Stack stay below. The reorder is a stable sort on a priority weight
    // so we don't accidentally drop or duplicate any item.
    if (fundraisingMode && investMode === "founder") {
      const PRIORITY: Record<string, number> = {
        "/dashboard/invest/find-investors": 0,
        "/dashboard/invest/raising-sim": 1,
        "/dashboard/invest/warm-intros": 2,
        "/dashboard/invest/updates": 3,
        "/dashboard/invest/outreach-templates": 4,
        "/dashboard/invest/decks": 5,
        "/dashboard/invest/datarooms": 6,
        "/dashboard/pipeline-crm?mode=founder": 7,
        "/dashboard/my-thesis?mode=founder": 8,
        "/dashboard/my-cap-table?mode=founder": 9,
        "/dashboard/signals": 10,
        "/dashboard/outreach": 11,
      };
      baseItems = [...baseItems].sort((a, b) => {
        const pa = PRIORITY[a.href] ?? 100;
        const pb = PRIORITY[b.href] ?? 100;
        return pa - pb;
      });
    }
    // INVEST-MODE GATE (Dispatch D): items flagged requiresInvestMode only show
    // when invest_mode is 'investor' or 'both'. Unflagged items always show.
    const investorAccess = hasInvestorAccess(investMode);
    const items = baseItems.filter((it) => !it.requiresInvestMode || investorAccess);
    // OFF HIDES THE WHOLE SECTION (2026-08-13). Not greyed out, not an empty
    // group — gone. A founder who does not invest should not be reading past
    // "Invest" in his sidebar every day to get to Engage. The switch back lives
    // in Profile settings, and every INVEST page still says it is off if a
    // bookmark lands there.
    if (investMode === "off") return null;
    // Unset shows the selector rather than assuming investor, which is what the
    // old resolveInvestMode default did on every page inside this section.
    const showSelector = !investMode || investSelectorOpen;
    // ALL-SUITES-COLLAPSED (2026-09-01): INVEST is no exception — it starts
    // collapsed on every load. The old route-force-open (expand whenever the
    // route was an INVEST item) is gone; investOpen is the sole control.
    const investExpanded = investOpen;
    const renderItem = (item: NavItem) => {
      // Exact-match active (deepest match for these flat routes) so a route
      // highlights ITS OWN item — never a parent like Overview (/dashboard/invest).
      const isActive = location.pathname === item.href || `${location.pathname}${location.search}` === item.href;
      return (
        <SidebarMenuItem key={item.name}>
          <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
            <NavLink to={item.href} end onClick={(e) => handleNavClick(item, e)} data-foxchat-id={item.foxchatId} className="flex items-center gap-2">
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.name}</span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    };

    // Icon-rail (sidebar collapsed): render mode items as plain icons; the
    // selector/switch UI only makes sense in the expanded sidebar.
    if (collapsed) {
      return (
        <SidebarGroup key="invest">
          <SidebarGroupContent>
            <SidebarMenu>{items.map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      );
    }

    return (
      <SidebarGroup key="invest">
        <SidebarGroupLabel asChild className="text-sidebar-foreground/50 uppercase tracking-wider text-[10px]">
          <button
            type="button"
            onClick={() => { if (!investExpanded) setInvestOpen(true); else setInvestOpen((o) => !o); }}
            className="w-full flex items-center gap-1.5 hover:text-sidebar-foreground transition-colors cursor-pointer"
            aria-expanded={investExpanded}
            title="Founder or investor? Pick a mode and this section tailors itself to your work."
            data-foxchat-id="nav-invest-header"
          >
            <FoxMark size={12} className="shrink-0" />
            <span className="flex-1 text-left">Invest</span>
            {investExpanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
          </button>
        </SidebarGroupLabel>
        {investExpanded && (
          <SidebarGroupContent>
            {showSelector ? (
              <div className="px-2 py-1.5 space-y-1.5" data-foxchat-id="invest-mode-selector">
                <p className="text-[11px] text-muted-foreground px-1">What's your mode?</p>
                <button
                  type="button"
                  onClick={() => pickInvestMode("founder")}
                  title="I'm raising. Find investors, run pipeline, prep pitches."
                  className="w-full text-left rounded-lg border border-border/60 bg-background hover:border-primary/50 hover:bg-primary/5 transition-colors px-2.5 py-2"
                >
                  <div className="text-[12px] font-semibold text-foreground">Founder</div>
                  <div className="text-[10px] text-muted-foreground leading-snug">I'm raising. Find investors, run pipeline, prep pitches.</div>
                </button>
                <button
                  type="button"
                  onClick={() => pickInvestMode("investor")}
                  title="I'm investing. Find deals, vet founders, run dealflow."
                  className="w-full text-left rounded-lg border border-border/60 bg-background hover:border-primary/50 hover:bg-primary/5 transition-colors px-2.5 py-2"
                >
                  <div className="text-[12px] font-semibold text-foreground">Investor</div>
                  <div className="text-[10px] text-muted-foreground leading-snug">I'm investing. Find deals, vet founders, run dealflow.</div>
                </button>
                <button
                  type="button"
                  onClick={() => pickInvestMode("off")}
                  title="Neither. Hide Invest and everything in it."
                  className="w-full text-left rounded-lg border border-border/60 bg-background hover:border-primary/50 hover:bg-primary/5 transition-colors px-2.5 py-2"
                  data-foxchat-id="invest-mode-off"
                >
                  <div className="text-[12px] font-semibold text-foreground">Neither — turn this off</div>
                  <div className="text-[10px] text-muted-foreground leading-snug">Hides this whole section: thesis, cap table, investor matching, deal vetting. Nothing is deleted. Turn it back on in Profile settings.</div>
                </button>
              </div>
            ) : (
              <>
                <SidebarMenu>{items.map(renderItem)}</SidebarMenu>
                <div className="px-3 pt-1.5">
                  <button
                    type="button"
                    onClick={() => setInvestSelectorOpen(true)}
                    title="Switch between Founder and Investor mode."
                    className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                    data-foxchat-id="invest-switch-mode"
                  >
                    Switch mode
                  </button>
                  <button
                    type="button"
                    onClick={() => pickInvestMode("off")}
                    title="Hide Invest and everything in it. Nothing is deleted; turn it back on in Profile settings."
                    className="ml-3 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                    data-foxchat-id="invest-turn-off"
                  >
                    Turn off
                  </button>
                </div>
              </>
            )}
          </SidebarGroupContent>
        )}
      </SidebarGroup>
    );
  };

  return (
    <>
    <style>{`
      @keyframes subtlePulse {
        0% { box-shadow: 0 0 0 0 rgba(34,211,238,0.45); }
        100% { box-shadow: 0 0 0 8px rgba(34,211,238,0); }
      }
      .engage-badge-pulsing { animation: subtlePulse 1.6s ease-out 3; }
    `}</style>
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      {/* Identity Block replaces old header */}
      <SidebarHeader className="p-0">
        <SidebarIdentityBlock profile={profile} />
      </SidebarHeader>

      {/* Navigation Content */}
      <SidebarContent>
        {/* Brand-new-user CTA — only shown once the collapse state has loaded
            so it doesn't flicker in then disappear on established accounts. */}
        {collapseLoaded && isBrandNew && !collapsed && !quietLineOn && (
          <div className="px-3 pt-3 pb-1">
            <NavLink
              to="/dashboard/my-den"
              data-foxchat-id="onboarding-finish-den-cta"
              className="block rounded-lg border-2 border-amber-500/50 bg-gradient-to-b from-amber-500/20 to-amber-500/5 px-3 py-3 hover:from-amber-500/30 hover:to-amber-500/10 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-amber-400 uppercase tracking-wider leading-tight">Start here</p>
                  <p className="text-sm font-semibold text-foreground leading-tight mt-0.5">Finish setting up your Den</p>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-400 shrink-0" />
              </div>
            </NavLink>
          </div>
        )}

        {navigation.map((section) => {
          // INVEST is special-cased — mode selector + mode-appropriate items.
          if (section.key === "invest") return renderInvestGroup();
          const groupKey = section.key;
          const isCollapsible = !!section.collapsible && !!groupKey;
          // ALL-SUITES-COLLAPSED (2026-09-01): one rule for every collapsible
          // group — collapsed unless the user opened it in THIS session. No
          // per-suite special cases, no route-force-open (sitting on an in-group
          // route used to pin a suite open and defeat its own toggle), and no
          // persisted map that could re-open one on load.
          const manualOpen = !!openGroups[groupKey!];
          const isGroupCollapsed = isCollapsible ? computeGroupCollapsed(false, manualOpen) : false;

          // DEN-SWITCHER-PILL (2026-05-17) — filter items by active Den.
          // While the filter state is still loading, show everything (no
          // flicker on first paint). Sections whose children all hide get
          // dropped entirely so we don't render naked section headers.
          const filteredItems = denFilterLoaded
            ? section.items.filter((item) =>
                // Coming-soon items always render (greyed) regardless of Den.
                item.comingSoonLabel || shouldShowRouteForDen(item.href, activeDenKey, showAllSurfaces),
              )
            : section.items;
          if (denFilterLoaded && section.section && filteredItems.length === 0) {
            return null;
          }
          return (
          <SidebarGroup key={section.section || "top"}>
            {section.section && (
              <SidebarGroupLabel
                asChild={isCollapsible}
                className="text-sidebar-foreground/50 uppercase tracking-wider text-[10px]"
              >
                {isCollapsible ? (
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupKey!)}
                    className="w-full flex items-center gap-1.5 hover:text-sidebar-foreground transition-colors cursor-pointer"
                    aria-expanded={!isGroupCollapsed}
                  >
                    {section.emoji && <span className="text-[12px] leading-none shrink-0" aria-hidden="true"><IconGlyph value={section.emoji} size={12} /></span>}
                    <span className="flex-1 text-left">{section.section}</span>
                    {/* The section says only "there is something in here". What
                        it is belongs to the item, one click away. */}
                    {sectionLight(section.key || "") > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-400" aria-label={`${sectionLight(section.key || "")} waiting`} />
                        </TooltipTrigger>
                        <TooltipContent>
                          {sectionLight(section.key || "")} thing{sectionLight(section.key || "") === 1 ? "" : "s"} in here need you. Open the section to see which.
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {/* THE PILL, ON THE SUITE HEADING — ONLY WHILE COLLAPSED.
                        The total across every page inside. The moment the suite
                        opens this disappears and the number reappears on the
                        pages that actually hold the items. */}
                    {isGroupCollapsed && navCounts.forSuite(section.key || "") > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="shrink-0 bg-amber-500 text-white text-[10px] min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full">
                            {navCounts.forSuite(section.key || "")}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[320px]">
                          {navCounts.suiteTooltip(section.key || "", section.section)}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {isGroupCollapsed
                      ? <ChevronRight className="w-3 h-3 shrink-0" />
                      : <ChevronDown className="w-3 h-3 shrink-0" />}
                  </button>
                ) : (
                  <span>{section.section}</span>
                )}
              </SidebarGroupLabel>
            )}
            {!isGroupCollapsed && (
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredItems.map((item) => {
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={
                          item.comingSoonLabel
                            ? `${item.name} — ${item.comingSoonLabel}`
                            : item.name === "Blog"
                            ? "Read our latest posts on Medium"
                            : item.tooltip || item.name
                        }
                        className={cn(
                          item.locked && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        {(item as any).external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-foxchat-id={item.foxchatId}
                            className="flex items-center gap-2"
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="truncate">{item.name}</span>
                          </a>
                        ) : (item.locked || item.href === "#") ? (
                          // Non-route trigger (modal opener like "APIs & AI Agents",
                          // or a locked item). Rendered as a plain anchor so it never
                          // resolves to a route and never shows a spurious active state.
                          <a
                            href="#"
                            onClick={(e) => handleNavClick(item, e)}
                            data-foxchat-id={item.foxchatId}
                            className="flex items-center gap-2"
                            aria-disabled={item.locked || undefined}
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="truncate">{item.name}</span>
                          </a>
                        ) : (
                          <NavLink
                            to={item.href}
                            end
                            onClick={(e) => {
                              // Opening it is what clears it. Before navigation,
                              // so the light is already gone on arrival.
                              if ((item as any).lightKey) void clearLight((item as any).lightKey);
                              handleNavClick(item, e);
                            }}
                            data-foxchat-id={item.foxchatId}
                            className="flex items-center gap-2"
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="truncate">{item.name}</span>
                          </NavLink>
                        )}
                      </SidebarMenuButton>
                      {/* THE PILL, ON THE PAGE THAT HOLDS THE ITEMS.
                          Only rendered here — the suite heading carries it while
                          the suite is collapsed, so exactly one of the two shows
                          at any moment. A page with nothing gets no pill, and a
                          zero is never rendered as "0". */}
                      {(item as any).countKey && !collapsed && navCounts.forPage((item as any).countKey) > 0 && (
                        <SidebarMenuBadge className="bg-amber-500 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{navCounts.forPage((item as any).countKey)}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[320px]">
                              {navCounts.tooltipFor((item as any).countKey)}
                            </TooltipContent>
                          </Tooltip>
                        </SidebarMenuBadge>
                      )}
                      {(item as any).lightKey && lights[(item as any).lightKey] && !collapsed && (
                        <SidebarMenuBadge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{lights[(item as any).lightKey].count}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {lights[(item as any).lightKey].count} {lights[(item as any).lightKey].why}. Opening this clears the light; anything arriving afterwards lights it again.
                            </TooltipContent>
                          </Tooltip>
                        </SidebarMenuBadge>
                      )}
                      {item.comingSoonLabel && !item.badge && !collapsed && (
                        <SidebarMenuBadge className="text-[9px] uppercase tracking-wide text-muted-foreground pointer-events-none">Soon</SidebarMenuBadge>
                      )}
                      {item.name === "Company Profile" && !collapsed && !profile?.company_name && (
                        <SidebarMenuBadge>
                          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        </SidebarMenuBadge>
                      )}
                      {item.name === "Engage" && engagePendingCount > 0 && !collapsed && (
                        <SidebarMenuBadge
                          className={cn(
                            "bg-cyan-500 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full",
                            badgePulsing && "engage-badge-pulsing"
                          )}
                        >
                          {engagePendingCount}
                        </SidebarMenuBadge>
                      )}
                      {item.name === "CC Queue" && ccPendingCount > 0 && !collapsed && (
                        <SidebarMenuBadge className="bg-amber-500 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
                          {ccPendingCount}
                        </SidebarMenuBadge>
                      )}
                      {item.name === "Data Quality" && dataQualityCount > 0 && !collapsed && (
                        <SidebarMenuBadge className="bg-amber-500 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
                          {dataQualityCount}
                        </SidebarMenuBadge>
                      )}
                      {item.name === "Saved Content" && savedContentBadgeCount > 0 && !collapsed && (
                        <SidebarMenuBadge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
                          {savedContentBadgeCount}
                        </SidebarMenuBadge>
                      )}
                      {item.name === "Welcome" && welcomeUnviewedCount > 0 && !collapsed && (
                        <SidebarMenuBadge className="bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
                          {welcomeUnviewedCount}
                        </SidebarMenuBadge>
                      )}
                      {item.badge === "Soon" && !collapsed && (
                        <SidebarMenuBadge className="bg-muted text-muted-foreground text-[10px]">
                          Soon
                        </SidebarMenuBadge>
                      )}
                      {item.badge === "New" && !collapsed && (
                        <SidebarMenuBadge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px]">
                          New
                        </SidebarMenuBadge>
                      )}
                      {item.badge === "Agency+" && !collapsed && (
                        <SidebarMenuBadge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[9px]">
                          Agency+
                        </SidebarMenuBadge>
                      )}
                      {item.badge === "ENTERPRISE" && !collapsed && (
                        <SidebarMenuBadge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[9px] font-bold tracking-wide">
                          ENTERPRISE
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
            )}
          </SidebarGroup>
          );
        })}
      </SidebarContent>

      {/* Footer with admin/owner links */}
      <SidebarFooter className="border-t border-sidebar-border">
        {!collapsed && !recordMode && profile?.is_owner && (
          <div className="px-2 py-2">
            <NavLink
              to="/owner"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-yellow-950/30 text-yellow-400 hover:bg-yellow-950/50 transition-colors"
            >
              <Crown className="h-4 w-4" />
              Owner Panel
            </NavLink>
          </div>
        )}
        {/* T779: the bottom-left ADMIN / Admin Panel links and the recording-mode link are
            gone. Admins switch with the "Admin view" switch in the top header (TopBar). */}
        {/* LOG OUT (2026-06-14) — last item in the left nav. Clears the Supabase
            session via the app's supabase client, then lands the user on /auth.
            Always rendered (not gated on admin/owner) and not hidden in record
            mode, so sign-out is always reachable. */}
        <div className="px-2 py-2">
          <button
            type="button"
            onClick={handleSignOut}
            title="Log out"
            data-foxchat-id="nav-log-out"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">Log out</span>}
          </button>
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>

    {/* WhatsNewPanel relocated to TopBar 2026-05-13 (next to the Den button). */}
    <FoxApisModal open={foxApisOpen} onOpenChange={setFoxApisOpen} />

    {/* RECORDING-MODE (2026-05-17): tiny "REC" badge bottom-right when
        ?rec=1 is in the URL. For Saul only — won't disrupt recordings
        (pointer-events:none) but tells him the mode is active. */}
    {recordMode && (
      <div
        className="fixed bottom-2 right-2 z-50 px-2 py-0.5 text-[10px] font-mono text-emerald-400 bg-black/40 rounded pointer-events-none"
        title="Recording mode — admin chrome hidden. Remove ?rec=1 from URL to restore."
      >
        REC
      </div>
    )}
    </>
  );
}

